import { Injectable, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, BehaviorSubject } from 'rxjs';

export interface ILanguageConfig {
  default: string;
  supported: Array<string>;
  locale: boolean;
}

export interface ILanguage {
  code: string;
}

export class Language {
  constructor(public code: string) {}
}

/**
 * Service is intended to manage the translation languages
 */
@Injectable()
export class LanguageService {
  // Standard language items
  private standardLangs: Array<ILanguage>;

  // Currect active translation language
  currentLanguageSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    ''
  );

  // All available languages considering configuration
  availableLanguages: Array<Language>;

  // Language configuration
  private languageConfig!: ILanguageConfig;

  constructor(
    private _translateService: TranslateService,
    @Inject('LOCALSTORAGE') private _localStorage: Storage
  ) {
    this.availableLanguages = new Array<Language>();
    this.standardLangs = new Array<ILanguage>();

    // Define standard languages that are already loaded intranslation service.
    this.standardLangs.push({
      code: 'en',
    });
  }

  /**
   * @method configure
   * Configure language for application
   * @param config ILanguageConfig config object
   */
  configure(config: ILanguageConfig): void {
    if (typeof config === 'undefined') {
      console.error('OCC_ERROR: Language configuration is not valid');
      return;
    }
    this.languageConfig = config;
    this.initialize();
  }

  /**
   * @method initialize
   * Initializes default language and the list of supported languages, sets locale
   * language. All settings are defined in configuration file.
   */
  initialize(): void {
    // Initialize standard languages
    // In order to set standard language ["en" by default] please uncomment the code snippet below
    // this.initLanguage("en");
    if (typeof this.languageConfig.supported !== 'undefined') {
      this.languageConfig.supported.forEach((code) => {
        this.initLanguage(code);
      });
    }

    // Set default language defined in configuration
    if (
      typeof this.languageConfig.default !== 'undefined' &&
      this.standardLangs.some(
        (lang) => lang.code === this.languageConfig.default
      )
    ) {
      this._translateService.setDefaultLang(this.languageConfig.default);
    } else {
      this._translateService.setDefaultLang('en');
    }

    // Use language by locale defined in configuration
    if (
      typeof this.languageConfig.locale !== 'undefined' &&
      this.languageConfig.locale
    ) {
      this.switchLanguage(this._translateService.getBrowserLang()!);
    } else {
      this.switchLanguage(this._translateService.defaultLang);
    }
  }

  /**
   * Initialize standard languages by the given code
   * @param code the language code
   */
  private initLanguage(code: string): void {
    const language = this.standardLangs.find((lang) => lang.code === code);
    if (language !== undefined) {
      this.addLanguage(language.code);
    } else {
      console.error('OCC_ERROR: Not supported: ', code);
      return;
    }
  }

  /**
   * Add translation language to application
   * @param code string the language code
   */
  private addLanguage(code: string): boolean {
    if (this.availableLanguages.some((lang) => lang.code === code)) {
      console.error('OCC_ERROR: Language has already been added');
      return false;
    }

    // Create language object
    const language: Language = new Language(code);

    this.availableLanguages.push(language);
    this._translateService.langs.push(language.code);

    return true;
  }

  /**
   * Switches the translation language
   * @param code string the language code
   */
  switchLanguage(code: string): Observable<any> {
    if (this.availableLanguages.some((language) => language.code === code)) {
      this.currentLanguageSubject.next(code);
      this._localStorage.setItem('language', code);
      return this._translateService.use(code);
    }

    console.warn('OCC_WARN: Cannot be switched to locale language:', code);
    return of(undefined);
  }
}
